import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Eye, Play, RefreshCw, ShieldCheck, ShieldOff, Landmark, Search, Waypoints } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { wiseSupplierReconciliationAPI } from '../../lib/api';
import { adminWiseSupplierTraceAPI } from '../../lib/adminWiseSupplierTraceApi';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { resolveReconciliationChipHref } from '../../lib/reconciliationArtifact';
import { useJobPolling as useSharedJobPolling, JobErrorBlock } from './JobPollingPanel';

const POLL_INTERVAL_MS = 2000;

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function fmtTimestamp(isoString) {
  if (!isoString) return '-';
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return isoString;
  return then.toLocaleString('en-GB');
}

const TIER_LABELS = {
  tier1_reference: 'Tier 1 (reference)',
  tier2_amount_date: 'Tier 2 (amount/date)',
  tier1_reference_corroborated: 'Tier 1 (corroborated)',
};

const REASON_LABELS = {
  none_found: 'no candidate found',
  ambiguous: 'ambiguous (multiple candidates)',
  reference_amount_mismatch: 'reference/amount mismatch',
  // Reason-honesty fix: corroboration WAS attempted against a reference-hit,
  // review_status-eligible invoice and failed exactly one of these four legs
  // -- never collapsed back into a generic review_status exclusion (the
  // INV-212287 prod incident this labels guard against).
  corroboration_failed_doc_type: 'corroboration failed: not a supplier bill (ACCREC, not ACCPAY)',
  corroboration_failed_supplier: 'corroboration failed: supplier mismatch',
  corroboration_failed_amount: 'corroboration failed: amount outside tolerance',
  corroboration_failed_date_window: 'corroboration failed: date outside 30-day window',
};

// br_os_paid_status is null whenever the invoice isn't linked to a BR OS
// invoice yet (or BR OS hasn't synced the raw status back) -- distinguish
// WHY from bros_push_status (joined in server-side from the confirmed
// match) instead of collapsing every "not known yet" case into a bare
// n/a, which reads identically to "the lookup is broken".
//
// needs_retry and needs_booking_pick used to both read as "booking
// pending", collapsing a real push failure (a push was attempted --
// bros_push_error is set -- and it errored) into the same label as
// "no unique booking found yet, nothing attempted" -- two very different
// situations for the operator to act on. bros_push_error is the dead-
// letter/push-failure signal (services/wise_supplier_reconciliation.py::
// auto_reconcile_transaction / retry_bros_push both set it only on a real
// failed push attempt) that distinguishes the two.
function brOsPaidLabel(evidence) {
  const status = evidence?.br_os_paid_status;
  if (status === 'paid') return 'paid in BR OS';
  if (status === 'partial' || status === 'unpaid') return `${status} in BR OS`;
  switch (evidence?.bros_push_status) {
    case 'attached':
      return 'pushed, awaiting BR OS sync';
    case 'needs_retry':
      return evidence?.bros_push_error ? 'push failed' : 'booking pending';
    case 'needs_booking_pick':
      return 'booking pending';
    default:
      return 'not yet pushed to BR OS';
  }
}

// Staged census (Part 2 of the "would reconcile 0 across 369" incident):
// per-row stage columns replacing the flat reason above with the actual
// failing stage, so the card doubles as the operator's to-do list -- e.g.
// "excluded by review_status=pending_review" reads as "go approve these
// invoices and re-Preview", not just "no candidate found".
function fmtReferenceExtracted(staged) {
  const re = staged?.reference_extracted;
  if (!re || !re.found) return 'none';
  return re.numbers.join(', ');
}

function fmtTier1(staged) {
  const t1 = staged?.tier1;
  if (!t1) return '-';
  if (t1.status === 'found_in_pool') return `hit (${t1.invoice_number || t1.invoice_id})`;
  if (t1.status === 'not_present_in_store') return 'not in store';
  if (t1.status === 'no_reference') return 'no reference';
  // Reason-honesty fix: a reference+review_status-eligible invoice WAS
  // found and corroboration WAS attempted against it -- report the
  // specific leg that failed, never the generic review_status exclusion
  // below (which would be dishonest here: corroboration ran and failed on
  // something else entirely).
  if (t1.status === 'corroboration_attempted_failed') {
    return `corroboration attempted (${t1.invoice_number || t1.invoice_id}), failed: ${t1.corroboration_failing_check}`;
  }
  if (t1.status === 'excluded') {
    const parts = (t1.excluded_by || []).map((r) => `${r.filter}=${r.value ?? '(missing)'}`);
    return `excluded by ${parts.join(', ')}`;
  }
  return t1.status || '-';
}

function fmtTier2(staged) {
  const t2 = staged?.tier2;
  if (!t2) return '-';
  if (t2.outcome === 'not_applicable') return '-';
  const count = t2.candidates_in_window;
  const countText = count == null ? '' : `${count} candidate${count === 1 ? '' : 's'}: `;
  return `${countText}${t2.outcome}`;
}

// ENQ/booking chip for the "Auto-approved by corroboration" list -- same
// destination as the Bank Transactions "BR Supplier · booking pending"
// chip (lib/reconciliationArtifact.js::resolveReconciliationChipHref,
// reused directly here rather than re-derived), so a match this card's own
// 6h scheduled retry (services/wise_supplier_reconciliation.py::
// scheduled_retry_pending_bros_attaches) later attaches a booking to shows
// the real ENQ ref instead of staying stuck on "booking pending" forever.
// Plain <a>, not react-router-dom's <Link>: this component already avoids
// react-router-dom entirely (see highlightMatchId below) because importing
// it crashes wiseSupplierReconciliationCard.test.jsx under Jest (react-
// router-dom v7 "Cannot find module 'react-router/dom'") -- same
// destination URL, same visual chip, just not the SPA-routed component.
function BookingRefChip({ matchId, bookingRef }) {
  const pending = !bookingRef;
  const label = bookingRef || 'booking pending';
  const badge = (
    <Badge variant="outline" className="text-[10px]" title={label}>
      {label}
    </Badge>
  );
  if (!pending) return badge;
  const href = resolveReconciliationChipHref({ booking_pending: true, match_id: matchId, match_missing: false });
  if (!href) return badge;
  return (
    <a href={href} data-testid={`wise-supplier-recon-corroborated-chip-${matchId}`}>
      {badge}
    </a>
  );
}

function BrosPushBadge({ status }) {
  if (!status) return null;
  const variant = status === 'attached' ? 'default' : status === 'needs_retry' ? 'destructive' : 'secondary';
  const label = status === 'attached' ? 'BR OS: attached'
    : status === 'needs_retry' ? 'BR OS: needs retry'
    : 'BR OS: needs booking pick';
  return <Badge variant={variant}>{label}</Badge>;
}

// FIX 3 (Prompt 16): a live-checked indicator for whether FA's configured
// BR OS API key actually round-trips against BR OS right now -- so Enable
// is never clicked against a dead link (the operator must read this before
// clicking Enable above, per the fixed contract).
function BrosLinkBadge({ link, loading }) {
  if (loading && !link) {
    return <Badge variant="secondary" data-testid="wise-supplier-recon-bros-link"><Loader2 className="h-3 w-3 animate-spin mr-1" />BR OS link: checking...</Badge>;
  }
  if (!link) return null;
  const variant = link.status === 'ok' ? 'default' : link.status === 'auth_failed' ? 'destructive' : 'secondary';
  const label = link.status === 'ok' ? 'BR OS link: ok'
    : link.status === 'auth_failed' ? 'BR OS link: auth failed'
    : 'BR OS link: unreachable';
  const title = [
    link.detail,
    link.last_checked ? `last checked ${fmtTimestamp(link.last_checked)}` : null,
  ].filter(Boolean).join(' — ');
  return <Badge variant={variant} data-testid="wise-supplier-recon-bros-link" title={title || undefined}>{label}</Badge>;
}

function useJobPolling({ onCompleted, onFailed, onProgress }) {
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);

  const stop = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback((jobId) => {
    stop();
    setPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await wiseSupplierReconciliationAPI.jobStatus(jobId);
        if (data.status === 'completed') {
          stop();
          onCompleted(data.result);
        } else if (data.status === 'failed') {
          stop();
          onFailed(data.error);
        } else if (data.status === 'running') {
          onProgress?.(data.progress || null);
        }
      } catch (err) {
        stop();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stop, onCompleted, onFailed, onProgress]);

  return { polling, start };
}

function ResultSummary({ operation, result }) {
  if (!result) return null;
  const reconciled = result.reconciled || [];
  const suggestions = result.suggestions || [];
  return (
    <div className="space-y-2 text-xs" data-testid={`wise-supplier-recon-${operation}-result`}>
      <div className="flex flex-wrap gap-3">
        <span>Cohort: <strong>{fmtInt(result.cohort_count)}</strong></span>
        <span className="text-green-600">
          {operation === 'preview' ? 'Would reconcile' : 'Reconciled'}: <strong>{fmtInt(result.reconciled_count)}</strong>
        </span>
        <span className="text-amber-600">Suggestions: <strong>{fmtInt(result.suggestions_count)}</strong></span>
        {result.wise_fee_excluded_count != null && (
          <span className="text-muted-foreground" data-testid={`wise-supplier-recon-${operation}-fee-excluded`}>
            Wise fee lines excluded from cohort: <strong>{fmtInt(result.wise_fee_excluded_count)}</strong>
          </span>
        )}
      </div>

      {reconciled.length > 0 && (
        <div className="border rounded overflow-auto max-h-72">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50 sticky top-0">
                <th className="text-left font-medium px-2 py-1">Transaction</th>
                <th className="text-left font-medium px-2 py-1">Invoice</th>
                <th className="text-left font-medium px-2 py-1">Tier</th>
                <th className="text-left font-medium px-2 py-1">Reference extracted</th>
                <th className="text-left font-medium px-2 py-1">Tier 1</th>
                <th className="text-left font-medium px-2 py-1">Tier 2</th>
              </tr>
            </thead>
            <tbody>
              {reconciled.map((r) => (
                <tr key={r.transaction_id} className="border-b last:border-b-0">
                  <td className="px-2 py-1 font-mono">{r.transaction_id}</td>
                  <td className="px-2 py-1 font-mono">{r.invoice_id}</td>
                  <td className="px-2 py-1">
                    {TIER_LABELS[r.tier] || r.tier || '-'}
                    {r.corroborated && (
                      <Badge variant="outline" className="ml-1 text-[10px]" title="review_status auto-approved through corroboration">
                        auto-approved
                      </Badge>
                    )}
                  </td>
                  <td className="px-2 py-1 text-muted-foreground truncate max-w-[160px]">{fmtReferenceExtracted(r.staged)}</td>
                  <td className="px-2 py-1 text-muted-foreground truncate max-w-[200px]">{fmtTier1(r.staged)}</td>
                  <td className="px-2 py-1 text-muted-foreground truncate max-w-[160px]">{fmtTier2(r.staged)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="border rounded overflow-auto max-h-72" data-testid={`wise-supplier-recon-${operation}-suggestions`}>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50 sticky top-0">
                <th className="text-left font-medium px-2 py-1">Transaction</th>
                <th className="text-left font-medium px-2 py-1">Reason</th>
                <th className="text-left font-medium px-2 py-1">Reference extracted</th>
                <th className="text-left font-medium px-2 py-1">Tier 1</th>
                <th className="text-left font-medium px-2 py-1">Tier 2</th>
                <th className="text-left font-medium px-2 py-1">Counterparty</th>
                <th className="text-left font-medium px-2 py-1">Amount</th>
                <th className="text-left font-medium px-2 py-1">Reference</th>
              </tr>
            </thead>
            <tbody>
              {suggestions.map((s) => (
                <tr key={s.transaction_id} className="border-b last:border-b-0" data-testid={`wise-supplier-recon-suggestion-${s.transaction_id}`}>
                  <td className="px-2 py-1 font-mono">{s.transaction_id}</td>
                  <td className="px-2 py-1 text-amber-600">{REASON_LABELS[s.reason] || s.reason}</td>
                  <td className="px-2 py-1 text-muted-foreground truncate max-w-[140px]">{fmtReferenceExtracted(s.staged)}</td>
                  <td className="px-2 py-1 text-muted-foreground truncate max-w-[220px]" title={fmtTier1(s.staged)}>{fmtTier1(s.staged)}</td>
                  <td className="px-2 py-1 text-muted-foreground truncate max-w-[160px]">{fmtTier2(s.staged)}</td>
                  <td className="px-2 py-1">{s.counterparty || '-'}</td>
                  <td className="px-2 py-1">{s.amount} {s.currency}</td>
                  <td className="px-2 py-1 text-muted-foreground truncate max-w-[160px]">{s.reference || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Part 1 of the invoice-store-census slice: read-only report answering why
// the candidate pool is empty/near-empty, straight from db.invoices (source
// gmail) -- never a code change, so it's safe to run any time, including
// directly against prod from the browser (this card's whole point).
function InvoiceStoreCensusReport({ census }) {
  if (!census) return null;
  const g = census.gmail_invoices;
  return (
    <div className="space-y-2 text-xs border rounded p-2" data-testid="wise-supplier-recon-invoice-store-census">
      <p className="font-medium">Invoice store census (source=gmail)</p>
      <div className="flex flex-wrap gap-3">
        <span>Total: <strong>{fmtInt(g.total)}</strong></span>
        <span>Eligible candidate pool: <strong>{fmtInt(census.eligible_candidate_pool_count)}</strong></span>
        <span>Issue date before {g.scope_start}: <strong>{fmtInt(g.issue_date_before_scope_start)}</strong></span>
        <span>Missing invoice_number: <strong>{fmtInt(g.missing_invoice_number)}</strong></span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <p className="text-muted-foreground">By review_status</p>
          {Object.entries(g.by_review_status).map(([k, v]) => (
            <p key={k}>{k}: <strong>{v}</strong></p>
          ))}
        </div>
        <div>
          <p className="text-muted-foreground">By payment_status</p>
          {Object.entries(g.by_payment_status).map(([k, v]) => (
            <p key={k}>{k}: <strong>{v}</strong></p>
          ))}
        </div>
        <div>
          <p className="text-muted-foreground">By matching_status</p>
          {Object.entries(g.by_matching_status).map(([k, v]) => (
            <p key={k}>{k}: <strong>{v}</strong></p>
          ))}
        </div>
      </div>
      <div>
        <p className="text-muted-foreground mb-1">Known reference lookups</p>
        <div className="border rounded overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left font-medium px-2 py-1">Supplier hint</th>
                <th className="text-left font-medium px-2 py-1">Invoice number</th>
                <th className="text-left font-medium px-2 py-1">Found?</th>
                <th className="text-left font-medium px-2 py-1">Detail</th>
              </tr>
            </thead>
            <tbody>
              {census.known_reference_lookups.map((l) => (
                <tr key={l.invoice_number} className="border-b last:border-b-0">
                  <td className="px-2 py-1">{l.supplier_hint}</td>
                  <td className="px-2 py-1 font-mono">{l.invoice_number}</td>
                  <td className="px-2 py-1">{l.found ? 'yes' : 'no'}</td>
                  <td className="px-2 py-1 text-muted-foreground">
                    {l.found
                      ? (l.eligible
                        ? 'eligible'
                        : `excluded by ${l.excluded_by.map((r) => `${r.filter}=${r.value ?? '(missing)'}`).join(', ')}`)
                      : (l.any_invoice_from_supplier.length > 0
                        ? `not under this number, but ${l.any_invoice_from_supplier.length} invoice(s) from a matching supplier name exist`
                        : 'no invoice from this supplier at all')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Read-only honesty check on the FA -> BR OS supplier-payment loop: every
// is_supplier_payment match FA confirmed more than 24h ago whose invoice's
// BR OS record isn't marked paid right now. Never fixes anything itself
// (CLAUDE.md: don't auto-correct evidence in the same pass) -- a nonzero
// divergent_count is a finding for the operator to act on, not something
// this report resolves.
function BrOsPaidDivergenceReport({ report }) {
  if (!report) return null;
  return (
    <div className="space-y-2 text-xs border rounded p-2" data-testid="wise-supplier-recon-divergence-report">
      <p className="font-medium">BR OS paid divergence (FA-reconciled 24h+ ago, not marked paid in BR OS)</p>
      <div className="flex flex-wrap gap-3">
        <span>Checked: <strong>{fmtInt(report.checked_count)}</strong></span>
        <span className={report.divergent_count > 0 ? 'text-amber-600' : 'text-green-600'}>
          Divergent: <strong>{fmtInt(report.divergent_count)}</strong>
        </span>
      </div>
      {report.rows.length > 0 && (
        <div className="border rounded overflow-auto max-h-72">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50 sticky top-0">
                <th className="text-left font-medium px-2 py-1">Invoice</th>
                <th className="text-left font-medium px-2 py-1">Supplier</th>
                <th className="text-left font-medium px-2 py-1">Transaction</th>
                <th className="text-left font-medium px-2 py-1">Reconciled</th>
                <th className="text-left font-medium px-2 py-1">BR OS status</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <tr
                  key={r.match_id}
                  className="border-b last:border-b-0"
                  data-testid={`wise-supplier-recon-divergence-row-${r.match_id}`}
                >
                  <td className="px-2 py-1 font-mono">{r.invoice_number || r.invoice_id}</td>
                  <td className="px-2 py-1">{r.supplier || '-'}</td>
                  <td className="px-2 py-1 font-mono">{r.transaction_id}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{fmtTimestamp(r.reconciled_at)}</td>
                  <td className="px-2 py-1">{brOsPaidLabel(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function WiseSupplierReconciliationCard() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [flagBusy, setFlagBusy] = useState(false);

  const [previewResult, setPreviewResult] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);

  const [applyResult, setApplyResult] = useState(null);
  const [applyStarting, setApplyStarting] = useState(false);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);

  const [recentRows, setRecentRows] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  // Deep-link target from the Bank Transactions "BR Supplier · booking
  // pending" chip (lib/reconciliationArtifact.js::resolveReconciliationChipHref)
  // -- scrolls to and highlights this match's row in "Recent
  // auto-reconciliations" below so attached-vs-waiting is never a bare
  // label with nothing behind it. Read directly off window.location rather
  // than react-router-dom's useLocation: this component fully unmounts/
  // remounts on navigation to /diagnostics (a distinct page), so a
  // mount-time read is sufficient, and it sidesteps a react-router-dom v7 /
  // Jest module-resolution issue ("Cannot find module 'react-router/dom'")
  // that a hook import would otherwise hit in wiseSupplierReconciliationCard.test.jsx.
  const highlightMatchId = new URLSearchParams(window.location.search).get('wise_supplier_match_id');
  const recentRowRefs = useRef({});

  const [census, setCensus] = useState(null);
  const [censusLoading, setCensusLoading] = useState(false);

  const [corroboratedRows, setCorroboratedRows] = useState([]);
  const [corroboratedLoading, setCorroboratedLoading] = useState(false);

  const [brosLink, setBrosLink] = useState(null);
  const [brosLinkLoading, setBrosLinkLoading] = useState(false);

  const [divergence, setDivergence] = useState(null);
  const [divergenceLoading, setDivergenceLoading] = useState(false);

  const [traceTxId, setTraceTxId] = useState('');
  const [traceResult, setTraceResult] = useState(null);
  const [traceStarting, setTraceStarting] = useState(false);
  const [traceFailed, setTraceFailed] = useState(null);

  const refreshStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const { data } = await wiseSupplierReconciliationAPI.status();
      setStatus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const refreshRecent = useCallback(async () => {
    setRecentLoading(true);
    try {
      const { data } = await wiseSupplierReconciliationAPI.recent();
      setRecentRows(data.rows || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRecentLoading(false);
    }
  }, []);

  const runInvoiceStoreCensus = useCallback(async () => {
    setCensusLoading(true);
    try {
      const { data } = await wiseSupplierReconciliationAPI.invoiceStoreCensus();
      setCensus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCensusLoading(false);
    }
  }, []);

  const refreshCorroborated = useCallback(async () => {
    setCorroboratedLoading(true);
    try {
      const { data } = await wiseSupplierReconciliationAPI.corroboratedApprovals();
      setCorroboratedRows(data.rows || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCorroboratedLoading(false);
    }
  }, []);

  const refreshBrosLink = useCallback(async () => {
    setBrosLinkLoading(true);
    try {
      const { data } = await wiseSupplierReconciliationAPI.brosLinkStatus();
      setBrosLink(data);
    } catch (err) {
      setBrosLink({ status: 'unreachable', detail: getErrorMessage(err) });
    } finally {
      setBrosLinkLoading(false);
    }
  }, []);

  const runDivergenceProbe = useCallback(async () => {
    setDivergenceLoading(true);
    try {
      const { data } = await wiseSupplierReconciliationAPI.brOsPaidDivergence();
      setDivergence(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDivergenceLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshStatus(); refreshRecent(); refreshCorroborated(); refreshBrosLink();
  }, [refreshStatus, refreshRecent, refreshCorroborated, refreshBrosLink]);

  useEffect(() => {
    if (!highlightMatchId) return;
    const el = recentRowRefs.current[highlightMatchId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightMatchId, recentRows]);

  const previewPoll = useJobPolling({
    onCompleted: useCallback((result) => {
      setPreviewResult(result);
      setPreviewFailed(null);
      setPreviewProgress(null);
      toast.success('Preview complete');
    }, []),
    onFailed: useCallback((error) => {
      setPreviewFailed(error);
      setPreviewProgress(null);
      toast.error(error ?? 'Preview run failed');
    }, []),
    onProgress: useCallback((progress) => {
      setPreviewProgress(progress);
    }, []),
  });

  const applyPoll = useJobPolling({
    onCompleted: useCallback((result) => {
      setApplyResult(result);
      setApplyFailed(null);
      setApplyProgress(null);
      refreshRecent();
      refreshCorroborated();
      toast.success('Run now complete');
    }, [refreshRecent, refreshCorroborated]),
    onFailed: useCallback((error) => {
      setApplyFailed(error);
      setApplyProgress(null);
      toast.error(error ?? 'Run now failed');
    }, []),
    onProgress: useCallback((progress) => {
      setApplyProgress(progress);
    }, []),
  });

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    setPreviewProgress(null);
    try {
      const { data } = await wiseSupplierReconciliationAPI.previewStart();
      if (data.job_id) {
        previewPoll.start(data.job_id);
      } else if (data.status === 'already_running') {
        toast.error('A preview run is already in progress.');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStarting(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm(
      'Run Wise supplier auto-reconciliation now? Matching transactions will be reconciled and ' +
      'pushed to BR OS immediately.'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    setApplyProgress(null);
    try {
      const { data } = await wiseSupplierReconciliationAPI.applyStart();
      if (data.job_id) {
        applyPoll.start(data.job_id);
      } else if (data.status === 'already_running') {
        toast.error('A run is already in progress.');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStarting(false);
    }
  };

  const toggleFlag = async () => {
    const next = !status?.enabled;
    if (next && !window.confirm(
      'Enable Wise supplier auto-reconciliation? The next scheduled run (every 30 minutes) and ' +
      'any Run Now click will auto-reconcile matching Wise outgoing payments and push evidence to BR OS.'
    )) return;
    setFlagBusy(true);
    try {
      await wiseSupplierReconciliationAPI.setFlag(next);
      await refreshStatus();
      toast.success(next ? 'Enabled' : 'Disabled');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setFlagBusy(false);
    }
  };

  const retryPush = async (row) => {
    setRetryingId(row.match_id);
    try {
      const { data } = await wiseSupplierReconciliationAPI.retryBrosPush(row.match_id, row.bros_booking_ref);
      if (data.ok) {
        toast.success('BR OS push succeeded');
      } else {
        toast.error(data.error || 'BR OS push failed again');
      }
      refreshRecent();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRetryingId(null);
    }
  };

  const needsPush = recentRows.filter((r) => r.bros_push_status === 'needs_retry' || r.bros_push_status === 'needs_booking_pick');

  const tracePoll = useSharedJobPolling({
    fetchStatus: adminWiseSupplierTraceAPI.traceJobStatus,
    onCompleted: useCallback((result) => {
      setTraceResult(result);
      setTraceFailed(null);
    }, []),
    onFailed: useCallback((error) => {
      setTraceFailed(error);
    }, []),
  });

  const runTrace = async () => {
    if (!traceTxId.trim()) return;
    setTraceStarting(true);
    setTraceFailed(null);
    setTraceResult(null);
    try {
      const { data } = await adminWiseSupplierTraceAPI.traceStart(traceTxId.trim());
      if (data.job_id) {
        tracePoll.start(data.job_id);
      }
    } catch (err) {
      setTraceFailed(getErrorMessage(err) || 'Failed to start trace.');
    } finally {
      setTraceStarting(false);
    }
  };

  const traceBusy = traceStarting || tracePoll.polling;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          {status?.enabled ? (
            <ShieldCheck className="h-4 w-4 text-amber-500" />
          ) : (
            <ShieldOff className="h-4 w-4 text-muted-foreground" />
          )}
          <Landmark className="h-4 w-4 text-muted-foreground" />
          Wise Supplier Auto-Reconciliation
          <BrosLinkBadge link={brosLink} loading={brosLinkLoading} />
        </CardTitle>
        <CardDescription>
          Outgoing Wise payments to suppliers, matched against Gmail-sourced and Xero-sourced (ACCPAY)
          supplier invoices (tier 1: invoice number in the payment reference, including multi-invoice
          references summing to the payment amount; tier 2: exact amount/currency/supplier within a
          0-14 day window), then pushed to BR OS as payment evidence. A Xero invoice marked Paid in
          Xero is never excluded here -- it's corroborating evidence once FA's own reference/supplier/
          amount/date checks agree, not a conflict. Scope hard-limited to Wise + outgoing. Default OFF
          -- Preview is safe to run any time, Run Now / the scheduled job only ever act while enabled.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid="wise-supplier-recon-refresh"
            size="sm" variant="outline" onClick={() => { refreshStatus(); refreshRecent(); }}
            disabled={statusLoading}
          >
            {statusLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh
          </Button>
          <Button
            data-testid="wise-supplier-recon-toggle-flag"
            size="sm" variant={status?.enabled ? 'outline' : 'default'}
            onClick={toggleFlag} disabled={flagBusy || !status}
          >
            {flagBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            {status?.enabled ? 'Disable' : 'Enable'}
          </Button>
          <Button
            data-testid="wise-supplier-recon-preview"
            size="sm" onClick={runPreview}
            disabled={previewStarting || previewPoll.polling}
          >
            {(previewStarting || previewPoll.polling) ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            Preview
          </Button>
          <Button
            data-testid="wise-supplier-recon-run-now"
            size="sm" variant="secondary" onClick={runApply}
            disabled={applyStarting || applyPoll.polling || !status?.enabled}
            title={!status?.enabled ? 'Enable the flag first' : undefined}
          >
            {(applyStarting || applyPoll.polling) ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />}
            Run now
          </Button>
          <Button
            data-testid="wise-supplier-recon-invoice-store-census-btn"
            size="sm" variant="outline" onClick={runInvoiceStoreCensus}
            disabled={censusLoading}
            title="Read-only: why is the candidate pool empty/near-empty? Never writes, safe against prod."
          >
            {censusLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
            Invoice store census
          </Button>
          <Button
            data-testid="wise-supplier-recon-divergence-btn"
            size="sm" variant="outline" onClick={runDivergenceProbe}
            disabled={divergenceLoading}
            title="Read-only: FA-reconciled supplier payments whose BR OS record isn't marked paid 24h+ later. Never writes."
          >
            {divergenceLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
            BR OS paid divergence
          </Button>
        </div>

        <InvoiceStoreCensusReport census={census} />

        {status && (
          <div className="flex flex-wrap gap-2 text-xs" data-testid="wise-supplier-recon-status">
            <Badge variant={status.enabled ? 'default' : 'secondary'}>
              {status.enabled ? 'enabled' : 'disabled'}
            </Badge>
            <span className="text-muted-foreground">
              cohort: <strong>{fmtInt(status.cohort_count)}</strong> unreconciled outgoing Wise transaction(s)
            </span>
            {status.enabled && status.enabled_at && (
              <span className="text-muted-foreground">enabled by {status.enabled_by || 'unknown'} at {fmtTimestamp(status.enabled_at)}</span>
            )}
            {!status.enabled && status.disabled_at && (
              <span className="text-muted-foreground">disabled by {status.disabled_by || 'unknown'} at {fmtTimestamp(status.disabled_at)}</span>
            )}
          </div>
        )}

        {previewPoll.polling && previewProgress && (
          <p className="text-xs text-muted-foreground" data-testid="wise-supplier-recon-preview-progress">
            Scanning: {fmtInt(previewProgress.scanned)} / {fmtInt(previewProgress.cohort)}
          </p>
        )}
        {previewFailed && <p className="text-red-600 dark:text-red-400 text-xs">{previewFailed}</p>}
        <ResultSummary operation="preview" result={previewResult} />

        {applyPoll.polling && applyProgress && (
          <p className="text-xs text-muted-foreground" data-testid="wise-supplier-recon-apply-progress">
            Scanning: {fmtInt(applyProgress.scanned)} / {fmtInt(applyProgress.cohort)}
          </p>
        )}
        {applyFailed && <p className="text-red-600 dark:text-red-400 text-xs">{applyFailed}</p>}
        <ResultSummary operation="apply" result={applyResult} />

        <div className="space-y-1">
          <p className="text-xs font-medium">Recent auto-reconciliations</p>
          {recentRows.length === 0 ? (
            <div className="text-xs text-muted-foreground" data-testid="wise-supplier-recon-recent-empty">
              No auto-reconciliations yet.
            </div>
          ) : (
            <div className="overflow-x-auto border rounded-lg max-h-56 overflow-y-auto" data-testid="wise-supplier-recon-recent">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50 sticky top-0">
                    <th className="text-left px-2 py-1 font-medium">Transaction</th>
                    <th className="text-left px-2 py-1 font-medium">Invoice</th>
                    <th className="text-left px-2 py-1 font-medium">ENQ</th>
                    <th className="text-left px-2 py-1 font-medium">When</th>
                    <th className="text-left px-2 py-1 font-medium">BR OS</th>
                    <th className="text-left px-2 py-1 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {recentRows.map((r) => (
                    <tr
                      key={r.match_id}
                      ref={(el) => { recentRowRefs.current[r.match_id] = el; }}
                      data-testid={`wise-supplier-recon-row-${r.match_id}`}
                      className={`border-b last:border-0 ${
                        r.match_id === highlightMatchId ? 'bg-amber-100 dark:bg-amber-900/40' : ''
                      }`}
                    >
                      <td className="px-2 py-1 font-mono">{r.transaction_id}</td>
                      <td className="px-2 py-1 font-mono">{r.invoice_number || r.invoice_id}</td>
                      <td className="px-2 py-1 font-mono">{r.bros_booking_ref || '-'}</td>
                      <td className="px-2 py-1 whitespace-nowrap">{fmtTimestamp(r.created_at)}</td>
                      <td className="px-2 py-1"><BrosPushBadge status={r.bros_push_status} /></td>
                      <td className="px-2 py-1">
                        {(r.bros_push_status === 'needs_retry' || r.bros_push_status === 'needs_booking_pick') && (
                          <Button
                            data-testid={`wise-supplier-recon-retry-${r.match_id}`}
                            size="sm" variant="outline"
                            onClick={() => retryPush(r)}
                            disabled={retryingId === r.match_id}
                          >
                            {retryingId === r.match_id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Retry'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {needsPush.length > 0 && (
            <p className="text-xs text-amber-600" data-testid="wise-supplier-recon-needs-push-count">
              {needsPush.length} row(s) need a BR OS push retry or booking pick.
            </p>
          )}
        </div>

        <div className="space-y-1">
          <p className="text-xs font-medium">Auto-approved by corroboration (last 50)</p>
          <p className="text-xs text-muted-foreground">
            Invoices whose review_status was pending_review/quarantined/null but were auto-approved
            because the full corroboration stack (reference + supplier + amount within bank-fee
            tolerance + payment date within the 30-day window) agreed. Audit this list before trusting
            the gate's behaviour.
          </p>
          {corroboratedLoading && corroboratedRows.length === 0 ? (
            <div className="text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Loading...</div>
          ) : corroboratedRows.length === 0 ? (
            <div className="text-xs text-muted-foreground" data-testid="wise-supplier-recon-corroborated-empty">
              No corroborated auto-approvals yet.
            </div>
          ) : (
            <div className="overflow-x-auto border rounded-lg max-h-56 overflow-y-auto" data-testid="wise-supplier-recon-corroborated">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50 sticky top-0">
                    <th className="text-left px-2 py-1 font-medium">Invoice</th>
                    <th className="text-left px-2 py-1 font-medium">Supplier</th>
                    <th className="text-left px-2 py-1 font-medium">Transaction</th>
                    <th className="text-left px-2 py-1 font-medium">Was</th>
                    <th className="text-left px-2 py-1 font-medium">Bank fee</th>
                    <th className="text-left px-2 py-1 font-medium">Date delta</th>
                    <th className="text-left px-2 py-1 font-medium">ENQ</th>
                    <th className="text-left px-2 py-1 font-medium">BR OS paid</th>
                    <th className="text-left px-2 py-1 font-medium">When</th>
                  </tr>
                </thead>
                <tbody>
                  {corroboratedRows.map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="px-2 py-1 font-mono">{r.invoice_number || r.invoice_id}</td>
                      <td className="px-2 py-1">{r.supplier || '-'}</td>
                      <td className="px-2 py-1 font-mono">{r.transaction_id}</td>
                      <td className="px-2 py-1 text-amber-600">{r.evidence?.pre_approval_review_status || '-'}</td>
                      <td className="px-2 py-1">{r.evidence?.bank_fee_amount ?? '-'}</td>
                      <td className="px-2 py-1">{r.evidence?.date_delta_days ?? '-'}d</td>
                      <td className="px-2 py-1"><BookingRefChip matchId={r.match_id} bookingRef={r.bros_booking_ref} /></td>
                      <td className="px-2 py-1">{brOsPaidLabel(r.evidence)}</td>
                      <td className="px-2 py-1 whitespace-nowrap">{fmtTimestamp(r.approved_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <BrOsPaidDivergenceReport report={divergence} />

        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center gap-2">
            <Waypoints className="h-4 w-4 text-muted-foreground" />
            <p className="text-xs font-medium">Per-transaction trace</p>
          </div>
          <p className="text-xs text-muted-foreground">
            Read-only, never writes. Given a Wise transaction id, reports the extracted reference
            tokens, the store lookup (doc id, source, ACCPAY/ACCREC type, review_status) for any
            invoice sharing that reference, whether corroboration was even eligible, and each
            corroboration check's pass/fail with values -- runs the exact same matcher this card's
            Preview/Apply use, so it can never diverge from what a live pass would compute for this row.
          </p>
          <div className="flex gap-2">
            <Input
              value={traceTxId}
              onChange={(e) => setTraceTxId(e.target.value)}
              placeholder="wise transaction id"
              className="h-8 text-xs"
              data-testid="wise-supplier-trace-transaction-id-input"
            />
            <Button
              variant="outline" size="sm" onClick={runTrace} disabled={traceBusy || !traceTxId.trim()}
              data-testid="wise-supplier-trace-run"
            >
              {traceBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Trace
            </Button>
          </div>

          <JobErrorBlock error={traceFailed} testId="wise-supplier-trace-error" />

          {traceResult && (
            traceResult.found === false ? (
              <p className="text-xs text-red-600" data-testid="wise-supplier-trace-not-found">
                Transaction {traceResult.transaction_id} not found.
              </p>
            ) : (
              <div className="space-y-2" data-testid="wise-supplier-trace-result">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span>Reference: <strong>{traceResult.reference || '(none)'}</strong></span>
                  <span>Counterparty: <strong>{traceResult.counterparty || '-'}</strong></span>
                  <span>Amount: <strong>{traceResult.amount} {traceResult.currency}</strong></span>
                  <Badge variant="outline" className="text-[10px]" data-testid="wise-supplier-trace-outcome">
                    {traceResult.matcher_outcome?.outcome}
                    {traceResult.matcher_outcome?.reason
                      ? `: ${REASON_LABELS[traceResult.matcher_outcome.reason] || traceResult.matcher_outcome.reason}`
                      : ''}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Reference tokens extracted: {traceResult.reference_extracted?.found
                    ? traceResult.reference_extracted.numbers.join(', ')
                    : 'none'}
                </p>

                {traceResult.store_lookup?.length === 0 ? (
                  <p className="text-xs text-muted-foreground" data-testid="wise-supplier-trace-no-store-hits">
                    No invoice in the store carries this reference number.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {traceResult.store_lookup?.map((entry) => (
                      <div
                        key={entry.invoice.id}
                        className="p-2 rounded border text-xs space-y-1"
                        data-testid={`wise-supplier-trace-invoice-${entry.invoice.id}`}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono">{entry.invoice.id}</span>
                          <Badge variant="outline" className="text-[10px]">{entry.invoice.source || 'unknown source'}</Badge>
                          <Badge variant="outline" className="text-[10px]">
                            {entry.invoice.xero_type_label || entry.invoice.type || 'unknown type'}
                          </Badge>
                          <Badge variant="outline" className="text-[10px]">
                            review_status: {entry.invoice.review_status ?? '(none)'}
                          </Badge>
                          <Badge
                            variant={entry.corroboration_eligible_review_status ? 'default' : 'secondary'}
                            className="text-[10px]"
                            data-testid={`wise-supplier-trace-eligibility-${entry.invoice.id}`}
                          >
                            {entry.corroboration_eligible_review_status
                              ? 'corroboration eligible'
                              : 'corroboration not attempted (review_status)'}
                          </Badge>
                        </div>
                        {entry.corroboration_checks && (
                          <ul className="space-y-0.5" data-testid={`wise-supplier-trace-checks-${entry.invoice.id}`}>
                            {['doc_type', 'supplier', 'amount', 'date_window'].map((checkName) => {
                              const check = entry.corroboration_checks[checkName];
                              if (!check) return null;
                              return (
                                <li key={checkName} className="flex flex-wrap items-center gap-2">
                                  <Badge
                                    variant={check.pass ? 'default' : 'destructive'}
                                    className="text-[10px]"
                                    data-testid={`wise-supplier-trace-check-${entry.invoice.id}-${checkName}`}
                                  >
                                    {checkName}: {check.pass ? 'pass' : 'fail'}
                                  </Badge>
                                  <span className="text-muted-foreground font-mono">
                                    {Object.entries(check)
                                      .filter(([k]) => k !== 'pass')
                                      .map(([k, v]) => `${k}=${v ?? 'null'}`)
                                      .join(' ')}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
}
